import React, { useState, useEffect } from 'react';

import { useBreadcrumbs, usePath } from '../../hooks';
import AdvancedStats from './AdvancedStats';
import { useAdvancedStats } from './useAdvancedStats';
import {
  useTeam,
  useTeamGames,
  useSeason,
  useSelector,
  validateSelectorValue,
} from '../shared/hooks';
import { teamName, seasonName, checkIfNBA } from '../../lib/format';
import { isArray } from 'util';
import { title } from './translations';
import { getConferenceOptions } from '../LineupExplorer/LineupsFilter/filterOptions';

const totalTitles = [
  'G',
  'MIN',
  'POS',
  'PTS',
  'FGM',
  'FGA',
  'FGM3',
  'FGA3',
  'FT',
  'FTA',
  'REBS',
  'OREBS',
  'DREBS',
  'AST',
  'TO',
  'STL',
  'BLK',
  'FOULS',
];

const rateTitles = [
  'EFG',
  'ORR',
  'FTR',
  'TOV',
  'FG%',
  'FG3%',
  'FG2%',
  'FT%',
  'FG3 PAR',
  'PPS',
  'TS',
  'USG',
];

function createBreadcrumbs(team, season, pathTo) {
  return [
    { title: 'Teams', url: pathTo('teams') },
    { title: `[${seasonName(season)}] ${teamName(team)}`, url: pathTo('team') },
    { title: 'Advanced Stats' },
  ];
}

const formatGameIds = value => {
  if (isArray(value)) {
    value = value.map(mapped => mapped.value).join(',');
  }
  return value;
};

const AdvancedStatsShow = ({ leagueId, seasonId, teamId }) => {
  const pathTo = usePath(leagueId, seasonId, teamId);
  const team = useTeam(teamId);
  const season = useSeason(seasonId);
  const [stageId, setStageId] = useState();
  const [gameIds, setGameIds] = useState();
  const isNBA = checkIfNBA(team);
  const division = useSelector(getConferenceOptions(), !isNBA && 'in');
  const conference = useSelector(getConferenceOptions());
  const [playerSort, setPlayerSort] = useState();
  const [activeTabIndex, setActiveTabIndex] = useState();
  const stats = useAdvancedStats(
    leagueId,
    seasonId,
    teamId,
    stageId,
    gameIds,
    playerSort,
    validateSelectorValue(division, getConferenceOptions()),
    validateSelectorValue(conference, getConferenceOptions()),
  );
  const games = useTeamGames(
    leagueId,
    seasonId,
    team,
    stageId,
    150,
    true,
    false,
    validateSelectorValue(division, getConferenceOptions()),
    validateSelectorValue(conference, getConferenceOptions()),
  );
  const totalSortOptions = totalTitles.map(value => ({
    value: value,
    label: title(value.toLowerCase()),
  }));
  const rateSortOptions = rateTitles.map(value => ({
    value: value,
    label: title(value.toLowerCase()),
  }));
  const groupedSortOptions = [
    { label: 'Totals', options: totalSortOptions },
    { label: 'Rates', options: rateSortOptions },
  ];
  const initialOption =
    playerSort &&
    totalSortOptions
      .concat(rateSortOptions)
      .find(element => element.value.toLowerCase() === playerSort);

  useBreadcrumbs(createBreadcrumbs, [team, season, pathTo]);

  const handleStageChange = stage => {
    if (stage) setStageId(stage.value);
  };

  const handleGameChange = gameIds => {
    if (gameIds) setGameIds(formatGameIds(gameIds));
    else setGameIds(null);
  };

  const handlePlayerSort = playerSort => {
    if (playerSort) setPlayerSort(playerSort.value.toLowerCase());
    else setPlayerSort(null);
  };

  const handleTabChange = tabIndex => {
    if (tabIndex) setActiveTabIndex(tabIndex);
    else setActiveTabIndex(0);
  };

  useEffect(() => {
    if (stageId) setStageId(stageId);
    else setStageId(null);
    if (gameIds) setGameIds(gameIds);
    else setGameIds(null);
    if (playerSort) setPlayerSort(playerSort);
    else setPlayerSort(null);
    if (activeTabIndex) setActiveTabIndex(activeTabIndex);
    else setActiveTabIndex(0);
  }, [stageId, gameIds, playerSort, activeTabIndex]);

  return (
    <AdvancedStats
      team={team}
      pathTo={pathTo}
      stats={stats}
      games={games}
      onStageChange={handleStageChange}
      onGameChange={handleGameChange}
      season={season}
      playerSort={initialOption}
      playerSortOptions={groupedSortOptions}
      onPlayerSort={handlePlayerSort}
      activeTabIndex={activeTabIndex}
      onTabChange={handleTabChange}
      division={division}
      conference={conference}
      isNBA={isNBA}
    />
  );
};

export default AdvancedStatsShow;
